/**
 * src/context/CartContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Persistent cart with dynamic coupon recalculation.
 *
 * BUG FIX: Coupon discount is now recalculated live whenever the cart subtotal
 * changes, instead of using the fixed discount amount from when coupon was applied.
 *
 * Logic mirrors backend Coupon.calculate_discount():
 *   percent → subtotal * discount_value / 100
 *   fixed   → min(discount_value, subtotal)
 *   if subtotal < min_order_value → discount = 0 (coupon invalid for this amount)
 *
 * Added: couponInvalid flag — true when subtotal drops below min_order_value.
 * CartPage reads this and shows a clear warning to the user.
 */
import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'lc_cart_v2';
const INITIAL = { items: [], coupon: null };

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const { product, qty = 1 } = action.payload;
      const existing = state.items.find(i => i.product.id === product.id);
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.product.id === product.id
              ? { ...i, qty: Math.min(i.qty + qty, 250) }
              : i
          ),
        };
      }
      return { ...state, items: [...state.items, { product, qty: Math.min(qty, 250) }] };
    }

    case 'REMOVE':
      return { ...state, items: state.items.filter(i => i.product.id !== action.id) };

    case 'UPDATE_QTY': {
      const { id, qty } = action.payload;
      if (qty <= 0) return { ...state, items: state.items.filter(i => i.product.id !== id) };
      return {
        ...state,
        items: state.items.map(i =>
          i.product.id === id
            ? { ...i, qty: Math.min(qty, 250) }
            : i
        ),
      };
    }

    case 'SET_COUPON':   return { ...state, coupon: action.payload };
    case 'CLEAR_COUPON': return { ...state, coupon: null };
    case 'CLEAR':        return { ...INITIAL };
    case 'HYDRATE':      return action.payload;

    default: return state;
  }
}

/**
 * Calculate coupon discount dynamically based on current subtotal and cart items.
 * Returns { discountAmount, couponInvalid, couponExcluded, excludedItems }
 *   couponInvalid  = true when subtotal < min_order_value
 *   couponExcluded = true when cart contains items from excluded categories
 *   excludedItems  = array of {name, category_name} for excluded items
 */
function calcCoupon(coupon, subtotal, items) {
  if (!coupon) return { discountAmount: 0, couponInvalid: false, couponExcluded: false, excludedItems: [] };

  const minOrder     = parseFloat(coupon.min_order_value || 0);
  const discountVal  = parseFloat(coupon.discount_value  || 0);
  const discountType = coupon.discount_type || 'percent';

  // Check excluded categories — gift-boxes and combo-packs cannot use this coupon
  const excludedSlugs = (coupon.excluded_category_slugs || 'gift-boxes,combo-packs')
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(Boolean);

  const excludedItems = (items || [])
    .filter(i => {
      const slug = (i.product.category_slug || '').toLowerCase();
      return excludedSlugs.includes(slug);
    })
    .map(i => ({ name: i.product.name, category_name: i.product.category_name }));

  if (excludedItems.length > 0) {
    // Cart has excluded items — coupon cannot be applied at all
    return { discountAmount: 0, couponInvalid: false, couponExcluded: true, excludedItems };
  }

  // Subtotal fell below coupon minimum — warn user, show 0 discount
  if (subtotal < minOrder) {
    return { discountAmount: 0, couponInvalid: true, couponExcluded: false, excludedItems: [] };
  }

  let amount = 0;
  if (discountType === 'percent') {
    amount = Math.round((subtotal * discountVal / 100) * 100) / 100;
  } else {
    amount = Math.min(discountVal, subtotal);
  }

  return { discountAmount: amount, couponInvalid: false, couponExcluded: false, excludedItems: [] };
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && Array.isArray(parsed.items)) {
          dispatch({ type: 'HYDRATE', payload: parsed });
        }
      }
    } catch { /* ignore corrupt data */ }
  }, []);

  // Persist on every state change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* storage full or private mode */ }
  }, [state]);

  // Clear cart on logout event
  useEffect(() => {
    const handler = () => dispatch({ type: 'CLEAR' });
    window.addEventListener('lc:cart-clear', handler);
    return () => window.removeEventListener('lc:cart-clear', handler);
  }, []);

  // ── Computed values ────────────────────────────────────────────────────────
  const subtotal = state.items.reduce(
    (sum, i) => sum + parseFloat(i.product.price) * i.qty, 0
  );

  // Dynamic coupon recalculation — mirrors backend calculate_discount()
  const { discountAmount, couponInvalid, couponExcluded, excludedItems } = calcCoupon(state.coupon, subtotal, state.items);

  const total     = Math.max(0, subtotal - discountAmount);
  const itemCount = state.items.reduce((sum, i) => sum + i.qty, 0);

  const value = {
    items:          state.items,
    coupon:         state.coupon,
    subtotal,
    discountAmount,
    couponInvalid,   // true when subtotal < coupon min_order_value
    couponExcluded,  // true when cart has items from excluded categories (gift-boxes, combo-packs)
    excludedItems,   // array of {name, category_name} for excluded items
    total,
    itemCount,

    isInCart:    (id)           => state.items.some(i => i.product.id === id),
    addItem:     (product, qty) => dispatch({ type: 'ADD',        payload: { product, qty } }),
    removeItem:  (id)           => dispatch({ type: 'REMOVE',     id }),
    updateQty:   (id, qty)      => dispatch({ type: 'UPDATE_QTY', payload: { id, qty } }),
    setCoupon:   (coupon)       => dispatch({ type: 'SET_COUPON', payload: coupon }),
    clearCoupon: ()             => dispatch({ type: 'CLEAR_COUPON' }),
    clearCart:   ()             => dispatch({ type: 'CLEAR' }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
};
